/**
 * iOS/iPadOS playback via Web Audio API.
 * Fully releases the mic before playback so iOS leaves playAndRecord mode
 * and routes TTS to the loudspeaker at normal volume.
 */

import { releaseMicStream } from "./micStreamManager";

const SILENT_MP3 =
  "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhAC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjQ1AAAAAAAAAAAAAAAAJAAAAAAAAYKb+pkTAAAAAAD/+0DEAAAHAAGf/+AIIgAA0gAAAAABNlbS4xMDAA";

const SESSION_SWITCH_DELAY_MS = 150;

let audioContext = null;
let gainNode = null;
let currentSource = null;
let unlockAudio = null;
let isUnlocked = false;
let fallbackObjectUrl = null;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getAudioContext = () => {
  if (!audioContext || audioContext.state === "closed") {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    gainNode = audioContext.createGain();
    gainNode.gain.value = 1.0;
    gainNode.connect(audioContext.destination);
  }
  return audioContext;
};

const revokeFallbackUrl = () => {
  if (fallbackObjectUrl) {
    URL.revokeObjectURL(fallbackObjectUrl);
    fallbackObjectUrl = null;
  }
};

const preparePlaybackSession = async () => {
  releaseMicStream();
  await delay(SESSION_SWITCH_DELAY_MS);

  const ctx = getAudioContext();
  if (ctx.state === "suspended") {
    await ctx.resume();
  }
};

const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const playWithHtmlAudioFallback = (base64, callbacks = {}) =>
  new Promise((resolve, reject) => {
    revokeFallbackUrl();

    if (!unlockAudio) {
      unlockAudio = new Audio();
      unlockAudio.preload = "auto";
      unlockAudio.playsInline = true;
      unlockAudio.setAttribute("playsinline", "");
      unlockAudio.setAttribute("webkit-playsinline", "");
    }

    const audio = unlockAudio;
    const audioBlob = new Blob(
      [Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))],
      { type: "audio/mpeg" }
    );
    fallbackObjectUrl = URL.createObjectURL(audioBlob);

    audio.volume = 1.0;
    audio.muted = false;
    audio.onplay = () => callbacks.onPlay?.();
    audio.onended = () => {
      revokeFallbackUrl();
      callbacks.onEnded?.();
    };
    audio.onerror = (event) => {
      revokeFallbackUrl();
      callbacks.onError?.(event);
    };

    audio.src = fallbackObjectUrl;
    audio.load();

    audio
      .play()
      .then(() => resolve(audio))
      .catch((error) => {
        callbacks.onError?.(error);
        reject(error);
      });
  });

/**
 * Call synchronously inside a click/tap handler before any await.
 */
export const unlockAudioPlayback = () => {
  if (isUnlocked) return;

  if (!unlockAudio) {
    unlockAudio = new Audio();
    unlockAudio.preload = "auto";
    unlockAudio.playsInline = true;
    unlockAudio.setAttribute("playsinline", "");
    unlockAudio.setAttribute("webkit-playsinline", "");
  }

  unlockAudio.src = SILENT_MP3;
  const playPromise = unlockAudio.play();
  if (!playPromise) return;

  playPromise
    .then(async () => {
      isUnlocked = true;
      unlockAudio.pause();
      unlockAudio.currentTime = 0;
      unlockAudio.removeAttribute("src");
      unlockAudio.load();

      const ctx = getAudioContext();
      if (ctx.state === "suspended") {
        await ctx.resume();
      }
    })
    .catch((error) => {
      console.warn("iOS audio unlock failed:", error);
    });
};

export const getSharedAudioElement = () => {
  if (!unlockAudio) {
    unlockAudio = new Audio();
    unlockAudio.playsInline = true;
    unlockAudio.setAttribute("playsinline", "");
    unlockAudio.setAttribute("webkit-playsinline", "");
  }
  return unlockAudio;
};

export const playBase64MpegAudio = async (base64, callbacks = {}) => {
  await preparePlaybackSession();
  stopAudioPlayback();

  const ctx = getAudioContext();

  try {
    const audioBuffer = await ctx.decodeAudioData(base64ToArrayBuffer(base64));

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(gainNode);
    currentSource = source;

    return new Promise((resolve, reject) => {
      source.onended = () => {
        currentSource = null;
        callbacks.onEnded?.();
      };

      try {
        callbacks.onPlay?.();
        source.start(0);
        resolve({ type: "ios-web-audio", source });
      } catch (error) {
        currentSource = null;
        callbacks.onError?.(error);
        reject(error);
      }
    });
  } catch (decodeError) {
    console.warn(
      "iOS Web Audio decode failed, falling back to HTMLAudioElement:",
      decodeError
    );
    return playWithHtmlAudioFallback(base64, callbacks);
  }
};

export const stopAudioPlayback = () => {
  if (currentSource) {
    try {
      currentSource.onended = null;
      currentSource.stop();
    } catch (error) {
      console.warn("Error stopping iOS audio source:", error);
    }
    currentSource = null;
  }

  if (unlockAudio) {
    unlockAudio.pause();
    unlockAudio.onplay = null;
    unlockAudio.onended = null;
    unlockAudio.onerror = null;
    revokeFallbackUrl();
  }
};

export const releaseAudioPlayback = () => {
  stopAudioPlayback();

  if (audioContext && audioContext.state !== "closed") {
    audioContext.close().catch(() => {});
  }

  audioContext = null;
  gainNode = null;
  isUnlocked = false;

  if (unlockAudio) {
    unlockAudio.removeAttribute("src");
    unlockAudio.load();
    unlockAudio = null;
  }
};
